import { faLongArrowRight } from '@fortawesome/pro-regular-svg-icons';
import React, { useCallback, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { useParams } from 'react-router-dom';
import moment from 'moment';

import Form, { SubmitType } from '../../../components/form';
import Button from '../../../components/form/elements/button';
import Group from '../../../components/form/elements/group';
import Link from '../../../components/form/elements/link';
import DatePeriod from '../../../components/form/elements/date-period';
import LabelTotal from '../../../components/form/elements/label-total';
import { LIVESTOCK_FODDER_NEED_INDEX } from '../../../router/routes';
import { route } from '../../../services/utils';
import { FormData } from './create';
import FodderNeedPeriodType from '../../../enums/fodder-need-period-type';
import css from './step-periods.module.css';

const schema = createPeriodsSchema(yup);

function createPeriodsSchema(schema: any) {
  const periods = [
    FodderNeedPeriodType.WINTER_SPRING,
    FodderNeedPeriodType.SUMMER,
    FodderNeedPeriodType.WINTER,
  ];

  let obj = {};
  periods.forEach((element) => {
    Object.assign(obj, {
      [element]: schema
        .object()
        .shape({
          from: schema
            .date()
            .nullable()
            .transform((_: any, value: any) => {
              return !value || value.length === 0 ? null : new Date(value);
            }),
          until: schema
            .date()
            .nullable()
            .transform((_: any, value: any) => {
              return !value || value.length === 0 ? null : new Date(value);
            }),
        })
        .test({
          name: 'winter_spring.from',
          message: 'lol2',
          path: 'winter_spring.from',
          test: function (values: any, context: any) {
            if (!values) {
              return false;
            }

            if (
              (values.from && !values.until) ||
              (!values.from && values.until)
            ) {
              return false;
            }

            if (values.from && values.until && values.from > values.until) {
              return false;
            }

            return true;
          },
        }),
    });
  });

  return schema
    .object()
    .shape({ periods: yup.object().shape(obj) })
    .test({
      test: function (values: any, context: any) {
        //check if one of periods has been fill in
        let canProceed = false;
        values = values.periods;
        Object.values(values).forEach((element: any) => {
          if (element.from && element.until) {
            canProceed = true;
          }
        });

        //check if periods do not overlop other periods
        Object.keys(values).forEach((key) => {
          Object.keys(values).forEach((key2) => {
            if (
              key !== key2 &&
              values[key].from &&
              values[key].until &&
              values[key2].from &&
              values[key2].until
            ) {
              if (
                moment(values[key].from).isBetween(
                  values[key2].from,
                  values[key2].until,
                  null,
                  '[]'
                ) ||
                moment(values[key].until).isBetween(
                  values[key2].from,
                  values[key2].until,
                  null,
                  '[]'
                )
              ) {
                canProceed = false;
              }
            }
          });
        });

        return canProceed;
      },
    });
}

export interface PeriodsSubmitData {
  periods: {
    [key: string]: {
      isSummerPeriod: boolean;
      from?: Date;
      until?: Date;
      id?: string;
    };
  };
  usedPeriods: string[];
  periodsDaysCount: { [key: string]: number };
}

export default function StepPeriods({
  formData,
  onSubmit,
}: {
  formData: FormData;
  onSubmit: SubmitType<PeriodsSubmitData>;
}) {
  const { t } = useTranslation();
  const tp = useCallback(
    (key: string) => t('pages.livestock.fodder_need.'.concat(key)),
    [t]
  );
  const { farmId } = useParams<{ farmId: string }>();

  const [footer] = useState(() => [
    Link(route(LIVESTOCK_FODDER_NEED_INDEX, { farmId: farmId }), 'cancel'),
    Button('continue', null, tp('continue'), faLongArrowRight),
  ]);

  const [total, setTotal] = useState(() => {
    const periods = formData.periods as any;
    let data = {} as any;
    Object.keys(periods).forEach((key) => {
      const cnt =
        periods[key].from && periods[key].until
          ? moment(periods[key].until)
              .startOf('day')
              .diff(moment(periods[key].from).startOf('day'), 'days') + 1
          : 0;
      data['periods.' + key] = cnt;
    });
    return data;
  });
  const [body, setBody] = useState([]);

  const countTotal = useCallback(
    (key: string, value: number) => {
      setTotal({ ...total, [key]: value });
    },
    [total]
  );

  useEffect(() => {
    setBody([
      Group(
        [
          DatePeriod(
            'periods.' + FodderNeedPeriodType.WINTER_SPRING,
            tp(FodderNeedPeriodType.WINTER_SPRING),
            countTotal
          ),
          DatePeriod(
            'periods.' + FodderNeedPeriodType.SUMMER,
            tp(FodderNeedPeriodType.SUMMER),
            countTotal
          ),
          DatePeriod(
            'periods.' + FodderNeedPeriodType.WINTER,
            tp(FodderNeedPeriodType.WINTER),
            countTotal
          ),
          LabelTotal(
            'total',
            Object.values(total)
              .reduce((a: any, b: any) => a + b, 0)
              .toString()
          ),
        ],
        tp('period')
      ),
    ]);
  }, [setBody, setTotal, total, tp, countTotal]);

  return (
    <div className={css['step-periods']}>
      <Form
        title={tp('fodder_need_calculation')}
        mode="step"
        body={body}
        footer={footer}
        values={formData}
        submit={onSubmit}
        schema={schema}
      />
    </div>
  );
}
